import * as Motion from 'motion';
import '@ungap/custom-elements';

window.theme = window.theme || {};

theme.HoverButtonEffect = class {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.onEnterHandler = this.onEnterHandler.bind(this);
    this.onLeaveHandler = this.onLeaveHandler.bind(this);
  }

  load() {
    if ((theme.config && theme.config.isTouch) || document.body.getAttribute('data-button_hover') === 'none') return;

    this.container.addEventListener('mouseenter', this.onEnterHandler);
    this.container.addEventListener('mouseleave', this.onLeaveHandler);
  }

  onEnterHandler(event) {
    const hoverButton = event.currentTarget;
    const btnFill = hoverButton.querySelector('[data-fill]');

    if (btnFill) {
      requestAnimationFrame(() => {
        Motion.animate(btnFill, { y: ['76%', '0%'] }, { duration: 0.6 });
      });
    }
  }

  onLeaveHandler(event) {
    const hoverButton = event.currentTarget;
    const btnFill = hoverButton.querySelector('[data-fill]');

    if (btnFill) {
      requestAnimationFrame(() => {
        Motion.animate(btnFill, { y: '-76%' }, { duration: 0.6 });
      });
    }
  }

  unload() {
    this.container.removeEventListener('mouseenter', this.onEnterHandler);
    this.container.removeEventListener('mouseleave', this.onLeaveHandler);
  }
};

class HoverElement extends HTMLElement {
  constructor() {
    super();
    this.hoverEffect = new theme.HoverButtonEffect(this);
  }

  connectedCallback() {
    this.hoverEffect.load();
  }

  disconnectedCallback() {
    this.hoverEffect.unload();
  }
}

customElements.define('hover-element', HoverElement);

class HoverButtonComponent extends HTMLButtonElement {
  constructor() {
    super();

    this.hoverEffect = new theme.HoverButtonEffect(this);
    this.hoverEffect.load();

    if (this.type === 'submit' && this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    window.addEventListener('pageshow', this.onPageShow.bind(this));

    this.append(this.animationElement);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement || (this._contentElement = this.querySelector('.btn-text'));
  }

  get animationElement() {
    if (!this._animationElement) {
      this._animationElement = document.createRange().createContextualFragment(`
        <span class="btn-loader">
          <span></span><span></span><span></span>
        </span>
      `).firstElementChild;
    }

    return this._animationElement;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'aria-busy' && newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }],
      ]);

      Motion.animate(
        this.animationElement.children,
        { transform: ['scale(1.6)', 'scale(0.6)'] },
        { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity }
      );
    } else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }],
      ]);
    }
  }

  onSubmit() {
    this.setAttribute('aria-busy', 'true');
  }

  onPageShow() {
    this.removeAttribute('aria-busy');
  }

  connectedCallback() {
    this.hoverEffect.load();
  }

  disconnectedCallback() {
    this.hoverEffect.unload();

    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit.bind(this));
    }

    window.removeEventListener('pageshow', this.onPageShow.bind(this));
  }
}

customElements.define('hover-button', HoverButtonComponent, { extends: 'button' });

class HoverLinkComponent extends HTMLAnchorElement {
  constructor() {
    super();
    this.hoverEffect = new theme.HoverButtonEffect(this);
  }

  connectedCallback() {
    this.hoverEffect.load();
  }

  disconnectedCallback() {
    this.hoverEffect.unload();
  }
}

customElements.define('hover-link', HoverLinkComponent, { extends: 'a' });
